import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Home, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const POPUP_STORAGE_KEY = 'roomia_welcome_popup_shown';
const POPUP_DURATION_HOURS = 48;
const LAUNCH_END_DATE = new Date('2026-04-27T23:59:59'); // 10 days from today

const WelcomePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if launch period is still active
    const now = new Date();
    if (now > LAUNCH_END_DATE) {
      return; // Don't show after launch period
    }

    // Check if popup was already shown recently
    const lastShown = localStorage.getItem(POPUP_STORAGE_KEY);
    if (lastShown) {
      const lastShownDate = new Date(parseInt(lastShown));
      const hoursSinceShown = (now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60);
      if (hoursSinceShown < POPUP_DURATION_HOURS) {
        return; // Don't show again within 48 hours
      }
    }

    // Show popup after 5-8 seconds (random delay for natural feel)
    const delay = 5000 + Math.random() * 3000;
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Mark as shown
      localStorage.setItem(POPUP_STORAGE_KEY, Date.now().toString());
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handlePublish = () => {
    handleClose();
    setTimeout(() => {
      navigate('/dashboard/new');
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleOverlayClick}
      data-testid="welcome-popup-overlay"
    >
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
      />
      
      {/* Modal */}
      <div
        className={`relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          animation: !isClosing ? 'popupEnter 0.4s ease-out' : undefined
        }}
        data-testid="welcome-popup-modal"
      >
        {/* Decorative gradient header */}
        <div className="h-2 bg-gradient-to-r from-primary via-blue-500 to-purple-500" />
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
          aria-label="Fermer"
          data-testid="welcome-popup-close"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className="p-6 pt-8 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20 flex items-center justify-center">
            <Home className="h-8 w-8 text-primary" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Bienvenue sur Roomia 👋
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
            Vous êtes propriétaire d'un logement meublé ?<br />
            Publiez votre bien dès maintenant et profitez d'une visibilité auprès de clients réellement intéressés.
          </p>

          {/* Launch offer badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Offre de lancement : visibilité boostée pendant 10 jours
            </span>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handlePublish}
              className="w-full h-12 text-base font-semibold gap-2 bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
              data-testid="welcome-popup-cta"
            >
              <Home className="h-5 w-5" />
              Publier mon logement
            </Button>
            
            <button
              onClick={handleClose}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              data-testid="welcome-popup-dismiss"
            >
              Fermer
            </button>
          </div>
        </div>

        {/* Footer note */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            🎯 Rejoignez les premiers propriétaires sur Roomia
          </p>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes popupEnter {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomePopup;
